import React from 'react';

import { TavernButton } from './TavernButton';
import { TavernEmptyState } from './TavernLoading';

type TavernErrorBoundaryProps = {
  children: React.ReactNode;
  /** Custom fallback UI to render when an error occurs */
  fallback?: React.ReactNode;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

type TavernErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

/**
 * Error Boundary component with Tavern theme styling.
 * Catches JavaScript errors anywhere in the child component tree and displays
 * a friendly fallback UI instead of crashing the whole app.
 *
 * @example
 * <TavernErrorBoundary>
 *   <MyComponent />
 * </TavernErrorBoundary>
 */
export class TavernErrorBoundary extends React.Component<
  TavernErrorBoundaryProps,
  TavernErrorBoundaryState
> {
  constructor(props: TavernErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): TavernErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to console in development
    console.error('TavernErrorBoundary caught an error:', error, errorInfo);

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="tavern-error-boundary">
          <TavernEmptyState
            icon="⚠️"
            title="Что-то пошло не так"
            description="Произошла непредвиденная ошибка. Попробуйте обновить страницу."
            action={
              <TavernButton variant="secondary" onClick={this.handleRetry}>
                Попробовать снова
              </TavernButton>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}
